import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Chart } from "chart.js/auto";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity,
  Building2,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  Gauge,
  Network,
  RefreshCw,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  UserCog,
  Users,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const EMPTY_DATA = {
  generatedAt: null,
  totals: {
    organizations: 0,
    activeOrganizations: 0,
    admins: 0,
    activeAdmins: 0,
    clients: 0,
    activeClients: 0,
    participants: 0,
    certificates: 0,
    requests: 0,
    pendingRequests: 0,
  },
  comparisons: {
    organizations30Days: { current: 0, previous: 0, growth: 0 },
    clients30Days: { current: 0, previous: 0, growth: 0 },
    certificates30Days: { current: 0, previous: 0, growth: 0 },
  },
  health: {
    activeOrganizationRate: 0,
    activeAdminRate: 0,
    activeClientRate: 0,
  },
  requests: { pending: 0, approved: 0, rejected: 0, registered: 0 },
  requestTypes: { Admin: 0, Client: 0 },
  organizationStatuses: [],
  monthlyTrend: [],
  organizations: [],
  recentOrganizations: [],
};

const numberFormatter = new Intl.NumberFormat("fr-FR");
const formatNumber = (value) => numberFormatter.format(Number(value) || 0);
const formatPercent = (value) => `${Number(value || 0).toFixed(1)} %`;

const formatMonth = (value, lang) => {
  if (!value) return "—";
  const [year, month] = value.split("-").map(Number);
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "fr-FR", {
    month: "short",
    year: "2-digit",
  }).format(new Date(year, month - 1, 1));
};

const formatDate = (value, lang) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

const GrowthBadge = ({ value }) => {
  const positive = Number(value) >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
        positive
          ? "bg-emerald-50 text-emerald-700"
          : "bg-rose-50 text-rose-700"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(Number(value) || 0).toFixed(1)} %
    </span>
  );
};

const KpiCard = ({ icon: Icon, title, value, detail, growth, tone = "blue" }) => {
  const styles = {
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    cyan: "bg-cyan-50 text-cyan-700 ring-cyan-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
  }[tone];

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex items-start justify-between gap-4">
        <span className={`rounded-xl p-3 ring-1 ${styles}`}>
          <Icon className="h-5 w-5" />
        </span>
        {growth !== undefined ? <GrowthBadge value={growth} /> : null}
      </div>
      <p className="mt-5 text-sm font-semibold text-slate-500">{title}</p>
      <p className="mt-1 text-3xl font-black tracking-tight text-slate-950">
        {formatNumber(value)}
      </p>
      <p className="mt-2 text-xs leading-5 text-slate-500">{detail}</p>
    </article>
  );
};

const ProgressLine = ({ label, value, detail }) => (
  <div>
    <div className="mb-2 flex items-center justify-between gap-3">
      <div>
        <p className="text-sm font-bold text-slate-800">{label}</p>
        <p className="text-xs text-slate-500">{detail}</p>
      </div>
      <span className="text-sm font-black text-blue-700">
        {formatPercent(value)}
      </span>
    </div>
    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
      <div
        className="h-full rounded-full bg-gradient-to-r from-blue-600 to-cyan-500"
        style={{ width: `${Math.min(Math.max(Number(value) || 0, 0), 100)}%` }}
      />
    </div>
  </div>
);

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const monthlyRef = useRef(null);
  const organizationStatusesRef = useRef(null);
  const chartsRef = useRef({});

  const tr = (key, fr, en) =>
    t?.ui?.superAdminDashboard?.[key] || (lang === "en" ? en : fr);

  const loadDashboard = async ({ silent = false } = {}) => {
    silent ? setRefreshing(true) : setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (!token) {
        navigate("/login", { replace: true });
        return;
      }
      if (!user?.isSuperAdmin && user?.adminRole !== "SuperAdmin") {
        navigate("/unauthorized", { replace: true });
        return;
      }

      const response = await axios.get(`${API_URL}/api/dashboard/platform`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData({ ...EMPTY_DATA, ...response.data });
    } catch (requestError) {
      console.error("Super Admin dashboard error:", requestError);
      setError(
        requestError.response?.data?.message ||
          tr(
            "loadError",
            "Impossible de charger le dashboard de la plateforme.",
            "Unable to load the platform dashboard."
          )
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (loading) return undefined;

    const destroy = (key) => {
      chartsRef.current[key]?.destroy();
      delete chartsRef.current[key];
    };

    if (monthlyRef.current) {
      destroy("monthly");
      const context = monthlyRef.current.getContext("2d");
      const gradient = context.createLinearGradient(0, 0, 0, 260);
      gradient.addColorStop(0, "rgba(37, 99, 235, 0.30)");
      gradient.addColorStop(1, "rgba(37, 99, 235, 0.01)");
      chartsRef.current.monthly = new Chart(context, {
        type: "line",
        data: {
          labels: data.monthlyTrend.map((item) => formatMonth(item.month, lang)),
          datasets: [
            {
              label: tr("certificates", "Certificats", "Certificates"),
              data: data.monthlyTrend.map((item) => item.count),
              borderColor: "#2563eb",
              backgroundColor: gradient,
              fill: true,
              tension: 0.38,
              pointRadius: 4,
              pointBackgroundColor: "#ffffff",
              pointBorderColor: "#2563eb",
              pointBorderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { grid: { display: false }, ticks: { color: "#64748b" } },
            y: {
              beginAtZero: true,
              grid: { color: "rgba(148, 163, 184, 0.16)" },
              ticks: { precision: 0, color: "#64748b" },
            },
          },
        },
      });
    }

    if (organizationStatusesRef.current) {
      destroy("organizationStatuses");
      chartsRef.current.organizationStatuses = new Chart(
        organizationStatusesRef.current,
        {
        type: "doughnut",
        data: {
          labels: data.organizationStatuses.map((item) => item.name),
          datasets: [
            {
              data: data.organizationStatuses.map((item) => item.count),
              backgroundColor: ["#2563eb", "#0891b2", "#8b5cf6", "#10b981"],
              borderColor: "#ffffff",
              borderWidth: 4,
              hoverOffset: 5,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "68%",
          plugins: {
            legend: {
              position: "bottom",
              labels: { usePointStyle: true, padding: 16, color: "#64748b" },
            },
          },
        },
      }
      );
    }

    return () => {
      Object.values(chartsRef.current).forEach((chart) => chart.destroy());
      chartsRef.current = {};
    };
  }, [loading, data.monthlyTrend, data.organizationStatuses, lang]);

  const topOrganizations = useMemo(
    () => [...(data.organizations || [])].slice(0, 10),
    [data.organizations]
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-3 text-sm font-semibold text-slate-600">
            {tr("loading", "Chargement de la plateforme...", "Loading platform...")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 pb-12 pt-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px]">
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-blue-950 to-indigo-950 px-6 py-7 text-white shadow-2xl sm:px-8">
          <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute -bottom-24 left-1/3 h-52 w-52 rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <span className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/15">
                <ShieldCheck className="h-7 w-7 text-cyan-300" />
              </span>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-300">
                  {tr("eyebrow", "Pilotage global", "Global management")}
                </p>
                <h1 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">
                  {tr("title", "Dashboard Super Admin", "Super Admin Dashboard")}
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
                  {tr(
                    "subtitle",
                    "Vue consolidée de toutes les organisations, administrateurs, clients et opérations de certification.",
                    "Consolidated view of all organizations, administrators, clients and certification operations."
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Link
                to="/organizations"
                className="inline-flex h-11 items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-bold text-white transition hover:bg-white/15"
              >
                <Network className="h-4 w-4" />
                {tr("organizations", "Organisations", "Organizations")}
              </Link>
              <Link
                to="/admins"
                className="inline-flex h-11 items-center gap-2 rounded-xl border border-cyan-300/20 bg-cyan-400/10 px-4 text-sm font-bold text-cyan-100 transition hover:bg-cyan-400/15"
              >
                <UserCog className="h-4 w-4" />
                {tr("admins", "Administrateurs", "Administrators")}
              </Link>
              <button
                type="button"
                disabled={refreshing}
                onClick={() => loadDashboard({ silent: true })}
                className="inline-flex h-11 items-center gap-2 rounded-xl bg-white px-4 text-sm font-black text-blue-900 transition hover:bg-blue-50 disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                {tr("refresh", "Actualiser", "Refresh")}
              </button>
            </div>
          </div>
        </section>

        {error ? (
          <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}

        <section className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <KpiCard
            icon={Building2}
            title={tr("organizations", "Organisations", "Organizations")}
            value={data.totals.organizations}
            growth={data.comparisons.organizations30Days?.growth}
            detail={`${formatNumber(data.totals.activeOrganizations)} ${tr(
              "active",
              "actives",
              "active"
            )}`}
          />
          <KpiCard
            icon={UserCog}
            title={tr("admins", "Administrateurs", "Administrators")}
            value={data.totals.admins}
            detail={`${formatNumber(data.totals.activeAdmins)} ${tr(
              "active",
              "actifs",
              "active"
            )}`}
            tone="violet"
          />
          <KpiCard
            icon={Users}
            title={tr("clients", "Clients", "Clients")}
            value={data.totals.clients}
            growth={data.comparisons.clients30Days?.growth}
            detail={`${formatNumber(data.totals.activeClients)} ${tr(
              "active",
              "actifs",
              "active"
            )}`}
            tone="cyan"
          />
          <KpiCard
            icon={FileCheck2}
            title={tr("certificates", "Certificats", "Certificates")}
            value={data.totals.certificates}
            growth={data.comparisons.certificates30Days?.growth}
            detail={`${formatNumber(data.totals.participants)} ${tr(
              "participants",
              "participants",
              "participants"
            )}`}
            tone="emerald"
          />
          <KpiCard
            icon={ClipboardList}
            title={tr("requests", "Demandes", "Requests")}
            value={data.totals.pendingRequests}
            detail={`${formatNumber(data.totals.requests)} ${tr(
              "total",
              "au total",
              "in total"
            )}`}
            tone="amber"
          />
        </section>

        <section className="mt-6 grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
          <article className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-blue-50 p-2 text-blue-700">
                  <Activity className="h-4 w-4" />
                </span>
                <div>
                  <h2 className="font-black text-slate-950">
                    {tr(
                      "production",
                      "Production globale",
                      "Global production"
                    )}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {tr(
                      "productionSubtitle",
                      "Certificats créés durant les six derniers mois",
                      "Certificates created during the last six months"
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="h-[310px] p-5">
              <canvas ref={monthlyRef} />
            </div>
          </article>

          <article className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="rounded-lg bg-violet-50 p-2 text-violet-700">
                  <Gauge className="h-4 w-4" />
                </span>
                <div>
                  <h2 className="font-black text-slate-950">
                    {tr("organizationStatuses", "Statuts des organisations", "Organization statuses")}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {tr(
                      "organizationStatusesSubtitle",
                      "Répartition des organisations selon leur état",
                      "Organization distribution by status"
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="h-[310px] p-5">
              {data.organizationStatuses.length ? (
                <canvas ref={organizationStatusesRef} />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                  {tr("noData", "Aucune donnée", "No data")}
                </div>
              )}
            </div>
          </article>
        </section>

        <section className="mt-6 grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
          <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <h2 className="font-black text-slate-950">
                  {tr(
                    "organizationPerformance",
                    "Performance par organisation",
                    "Performance by organization"
                  )}
                </h2>
                <p className="text-xs text-slate-500">
                  {tr(
                    "organizationPerformanceSubtitle",
                    "Classement selon le volume de certificats",
                    "Ranking by certificate volume"
                  )}
                </p>
              </div>
              <Link to="/organizations" className="text-sm font-bold text-blue-700">
                {tr("viewAll", "Tout voir →", "View all →")}
              </Link>
            </div>
            <div className="max-h-[470px] overflow-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="sticky top-0 bg-slate-900 text-xs uppercase tracking-wider text-slate-300">
                  <tr>
                    <th className="px-4 py-3">Organisation</th>
                    <th className="px-4 py-3 text-center">Admins</th>
                    <th className="px-4 py-3 text-center">Clients</th>
                    <th className="px-4 py-3 text-center">Participants</th>
                    <th className="px-4 py-3 text-center">Certificats</th>
                    <th className="px-4 py-3 text-center">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topOrganizations.map((organization) => (
                    <tr key={organization._id} className="hover:bg-blue-50/60">
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-900">{organization.name}</p>
                        <p className="text-xs text-slate-400">{organization.slug}</p>
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-slate-700">
                        {organization.admins}
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-slate-700">
                        {organization.clients}
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-slate-700">
                        {organization.participants}
                      </td>
                      <td className="px-4 py-3 text-center font-black text-blue-700">
                        {organization.certificates}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                            organization.status === "Active"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {organization.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <div className="space-y-6">
            <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="rounded-xl bg-emerald-50 p-2.5 text-emerald-700">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="font-black text-slate-950">
                    {tr("platformHealth", "Santé de la plateforme", "Platform health")}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {tr("accessAvailability", "Disponibilité des accès", "Access availability")}
                  </p>
                </div>
              </div>
              <div className="mt-6 space-y-5">
                <ProgressLine
                  label={tr("organizations", "Organisations", "Organizations")}
                  value={data.health.activeOrganizationRate}
                  detail={`${data.totals.activeOrganizations}/${data.totals.organizations}`}
                />
                <ProgressLine
                  label={tr("admins", "Administrateurs", "Administrators")}
                  value={data.health.activeAdminRate}
                  detail={`${data.totals.activeAdmins}/${data.totals.admins}`}
                />
                <ProgressLine
                  label={tr("clients", "Clients", "Clients")}
                  value={data.health.activeClientRate}
                  detail={`${data.totals.activeClients}/${data.totals.clients}`}
                />
              </div>
            </article>

            <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="font-black text-slate-950">
                {tr("recentOrganizations", "Organisations récentes", "Recent organizations")}
              </h2>
              <div className="mt-4 space-y-3">
                {data.recentOrganizations.map((organization) => (
                  <div
                    key={organization._id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-900">
                        {organization.name}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {organization.status} · {formatDate(organization.createdAt, lang)}
                      </p>
                    </div>
                    <span
                      className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                        organization.status === "Active"
                          ? "bg-emerald-500"
                          : "bg-slate-300"
                      }`}
                    />
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>
      </div>
    </main>
  );
};

export default SuperAdminDashboard;
